'use client'

import { ClockIcon } from '@repo/ui/ClockIcon'
import { Drawer } from '@repo/ui/Drawer'
import { EmptyState } from '@repo/ui/EmptyState'
import { formatDate } from '@repo/utils/time'
import { TimeLedgerPostingIcon } from '@repo/ui/TimeLedgerPostingIcon'
import { TimeRecordingActiveTimer } from './components/TimeRecordingActiveTimer/TimeRecordingActiveTimer'
import { TimeRecordingDateFilter } from './components/TimeRecordingDateFilter/TimeRecordingDateFilter'
import { TimeRecordingForm } from './components/TimeRecordingForm/TimeRecordingForm'
import { TimeRecordingHeader } from './components/TimeRecordingHeader/TimeRecordingHeader'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useTimeRecordingStore } from '@repo/store/useTimeRecordingStore'
import { TimeRecordingList } from './components/TimeRecordingList/TimeRecordingList'
import { useState, useEffect, useCallback, useRef } from 'react'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const TimeRecordingModule = ({ API_DI, forceDrawerOpen = false }) => {
  const [mode, setMode] = useState('timers')
  const [date, setDate] = useState(new Date())
  const [listData, setListData] = useState({})
  const [clientFilter, setClientFilter] = useState(null)
  const [matterFilter, setMatterFilter] = useState(null)
  const [showEmptyState, setShowEmptyState] = useState(false)
  const [selectedTimerId, setSelectedTimerId] = useState(null)
  const [fetchingData, setFetchingData] = useState(false)
  const { isOutlookAddin } = useGlobalSettingsStore()

  const scrollContainerRef = useRef(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMoreTimers, setHasMoreTimers] = useState(true)
  const [hasMorePostings, setHasMorePostings] = useState(true)
  const scrollDebounceRef = useRef(null)

  const lastScrollTopRef = useRef(0)
  const prevActiveTimerIdRef = useRef(null)

  const {
    timeRecordingDrawerIsOpen,
    setTimeRecordingDrawerIsOpen,
    fetchAllTimers,
    fetchAllPostings,
    activeTimer,
    allTimers,
    allPostings,
    fetchActiveTimer,
    setAllTimersApiParams,
    setAllPostingsApiParams,
    pauseTimer,
    timeRecordingFormToDisplay,
    setTimeRecordingFormToDisplay
  } = useTimeRecordingStore()

  useEffect(() => {
    setClientFilter(null)
    setMatterFilter(null)
    setSelectedTimerId(null)
  }, [timeRecordingDrawerIsOpen])

  useEffect(() => {
    setAllTimersApiParams({
      startAtIndex: 0,
      pageSize: 100,
      endDate: date,
      clientNo: clientFilter,
      matterNo: matterFilter
    })

    setAllPostingsApiParams({
      startAtIndex: 0,
      pageSize: 100,
      postingDate: date,
      clientNo: clientFilter,
      matterNo: matterFilter
    })

    setHasMoreTimers(true)
    setHasMorePostings(true)
    setIsLoadingMore(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientFilter, matterFilter])

  useEffect(() => {
    const currentTimerId = activeTimer?.matterTimerId ?? null

    if (currentTimerId !== prevActiveTimerIdRef.current) {
      setHasMoreTimers(true)
      setHasMorePostings(true)
      setIsLoadingMore(false)
      prevActiveTimerIdRef.current = currentTimerId
    }
  }, [activeTimer?.matterTimerId])

  useEffect(() => {
    let listData

    if (mode === 'timers') {
      const inactiveTimers = allTimers?.filter((timer) => timer.matterTimerId !== activeTimer?.matterTimerId)
      listData = groupByDate(inactiveTimers, 'timerPaused')
    } else if (mode === 'postings') {
      listData = groupByDate(allPostings, 'postingDate')
    }

    setListData(listData)
  }, [mode, allTimers, allPostings, activeTimer])

  const fetchTimerListData = useCallback(async () => {
    setFetchingData(true)
    setHasMoreTimers(true)

    setAllTimersApiParams({
      startAtIndex: 0,
      pageSize: 100,
      endDate: date,
      clientNo: clientFilter,
      matterNo: matterFilter
    })

    const result = await fetchAllTimers({ append: false })
    setHasMoreTimers(result?.hasMore ?? true)
    setFetchingData(false)
  }, [date, fetchAllTimers, clientFilter, matterFilter, setAllTimersApiParams])

  const fetchPostingListData = useCallback(async () => {
    setFetchingData(true)
    setHasMorePostings(true)

    setAllPostingsApiParams({
      startAtIndex: 0,
      pageSize: 100,
      postingDate: date,
      clientNo: clientFilter,
      matterNo: matterFilter
    })

    const result = await fetchAllPostings({ append: false })
    setHasMorePostings(result?.hasMore ?? true)
    setFetchingData(false)
  }, [date, fetchAllPostings, clientFilter, matterFilter, setAllPostingsApiParams])

  const loadMoreTimers = useCallback(async () => {
    if (isLoadingMore || !hasMoreTimers || fetchingData) return

    setIsLoadingMore(true)
    const result = await fetchAllTimers({ append: true })
    setHasMoreTimers(result?.hasMore ?? false)
    setIsLoadingMore(false)
  }, [fetchAllTimers, isLoadingMore, hasMoreTimers, fetchingData])

  const loadMorePostings = useCallback(async () => {
    if (isLoadingMore || !hasMorePostings || fetchingData) return

    setIsLoadingMore(true)
    const result = await fetchAllPostings({ append: true })
    setHasMorePostings(result?.hasMore ?? false)
    setIsLoadingMore(false)
  }, [fetchAllPostings, isLoadingMore, hasMorePostings, fetchingData])

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const currentScrollTop = container.scrollTop

    if (currentScrollTop === lastScrollTopRef.current) {
      return
    }

    lastScrollTopRef.current = currentScrollTop

    if (scrollDebounceRef.current) {
      clearTimeout(scrollDebounceRef.current)
    }

    scrollDebounceRef.current = setTimeout(() => {
      const { scrollTop, scrollHeight, clientHeight } = container

      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50

      if (isNearBottom && !isLoadingMore && !fetchingData) {
        if (mode === 'timers' && hasMoreTimers) {
          loadMoreTimers()
        } else if (mode === 'postings' && hasMorePostings) {
          loadMorePostings()
        }
      }
    }, 200)
  }, [mode, isLoadingMore, hasMoreTimers, hasMorePostings, fetchingData, loadMoreTimers, loadMorePostings])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    container.addEventListener('scroll', handleScroll)

    return () => {
      container.removeEventListener('scroll', handleScroll)
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current)
      }
    }
  }, [handleScroll])

  useEffect(() => {
    setHasMoreTimers(true)
    setHasMorePostings(true)
    setIsLoadingMore(false)
  }, [mode, date])

  useEffect(() => {
    if (API_DI || timeRecordingFormToDisplay) return

    if (mode === 'timers') {
      fetchTimerListData()
    } else if (mode === 'postings') {
      fetchPostingListData()
    }
  }, [
    mode,
    date,
    fetchAllTimers,
    fetchAllPostings,
    clientFilter,
    matterFilter,
    API_DI,
    setAllTimersApiParams,
    setAllPostingsApiParams,
    timeRecordingFormToDisplay,
    fetchTimerListData,
    fetchPostingListData
  ])

  useEffect(() => {
    API_DI ? API_DI() : fetchActiveTimer()
  }, [fetchActiveTimer, API_DI])

  useEffect(() => {
    if (mode === 'timers') {
      setShowEmptyState(!allTimers || !allTimers.length)
    } else {
      setShowEmptyState(!allPostings || !allPostings.length)
    }
  }, [allTimers, allPostings, activeTimer, mode])

  useEffect(() => {
    if (timeRecordingFormToDisplay !== 'postTimer') setSelectedTimerId(null)
  }, [timeRecordingFormToDisplay])

  const handleSelectTimer = async (timer) => {
    if (timer?.matterTimerId) {
      if (activeTimer?.isPlaying) await pauseTimer({ matterTimerId: activeTimer.matterTimerId })
      setSelectedTimerId(timer?.matterTimerId)
      const clientMatterNo = timer?.clientNo ? `${timer.clientNo}-${timer.matterNo}` : null
      setTimeout(() => {
        setTimeRecordingFormToDisplay('postTimer', clientMatterNo)
      }, 1)
    }
  }

  const closeForm = (formMode) => {
    if (formMode === 'timers' || timeRecordingFormToDisplay === 'createTimer') {
      setMode('timers')
    } else {
      setMode('postings')
    }

    fetchPostingListData()
    fetchTimerListData()

    setTimeRecordingFormToDisplay(null)
    setSelectedTimerId(null)
    fetchActiveTimer()
  }

  const hasMore = mode === 'timers' ? hasMoreTimers : hasMorePostings
  const currentData = mode === 'timers' ? allTimers : allPostings

  return (
    <div data-testid="TimeRecordingModule">
      <Drawer
        isOpen={timeRecordingDrawerIsOpen || forceDrawerOpen}
        onClose={() => setTimeRecordingDrawerIsOpen(false)}
        headingNode={<TimeRecordingDateFilter value={date} onChange={setDate} />}
        drawerSx={{
          top: isOutlookAddin ? '56px' : 0,
          height: isOutlookAddin ? 'calc(100% - 56px)' : '100%',
          '[data-testid="DrawerHeader"]': {
            display: isOutlookAddin ? 'none' : 'flex'
          },
          '.MuiBackdrop-root': { display: isOutlookAddin ? 'none' : 'flex' }
        }}
        sx={{
          paddingTop: isOutlookAddin ? '20px' : 0,
          top: isOutlookAddin ? '56px' : 0,
          height: isOutlookAddin ? 'calc(100% - 56px)' : '100%'
        }}
      >
        <MUIStack direction="column">
          <TimeRecordingHeader
            mode={mode}
            setMode={setMode}
            setClientFilter={setClientFilter}
            setMatterFilter={setMatterFilter}
            onSelectForm={(value) => setTimeRecordingFormToDisplay(value)}
          />
        </MUIStack>

        <MUIStack
          ref={scrollContainerRef}
          sx={{ padding: '20px 16px', flex: 1, overflowY: 'auto' }}
          direction="column"
          gap={'10px'}
        >
          {showEmptyState ? (
            <EmptyState
              sx={{ mt: -10 }}
              icon={
                mode === 'timers' ? (
                  <ClockIcon sx={{ fontSize: 36, mb: 4, opacity: 0.56 }} />
                ) : (
                  <TimeLedgerPostingIcon sx={{ fontSize: 36, mb: 4 }} />
                )
              }
              text={mode === 'timers' ? 'No timers found' : 'No time ledger postings found'}
            />
          ) : null}

          {mode === 'timers' && activeTimer ? (
            <TimeRecordingActiveTimer
              activeTimer={activeTimer}
              clientNo={clientFilter}
              matterNo={matterFilter}
              handleSelectTimer={handleSelectTimer}
            />
          ) : null}

          {fetchingData ? null : (
            <>
              <TimeRecordingList listData={listData} handleSelectTimer={handleSelectTimer} mode={mode} />
              {isLoadingMore && <MUITypography sx={{ textAlign: 'center', py: 2 }}>Loading more...</MUITypography>}
              {!hasMore && currentData?.length > 0 && (
                <MUITypography sx={{ textAlign: 'center', py: 2, opacity: 0.6 }}>
                  {mode === 'timers' ? 'No more timers to load' : 'No more postings to load'}
                </MUITypography>
              )}
            </>
          )}
        </MUIStack>
      </Drawer>

      <Drawer
        isOpen={timeRecordingFormToDisplay ? true : false}
        onClose={() => setTimeRecordingFormToDisplay(null)}
        headingNode={
          <MUITypography sx={{ mb: 0 }} variant="h5">
            {getFormTitle(timeRecordingFormToDisplay)}
          </MUITypography>
        }
      >
        <TimeRecordingForm
          formMode={timeRecordingFormToDisplay}
          closeForm={closeForm}
          selectedTimerId={selectedTimerId}
        />
      </Drawer>
    </div>
  )
}

TimeRecordingModule.propTypes = {
  API_DI: PropTypes.func,
  forceDrawerOpen: PropTypes.bool
}

const groupByDate = (rows, dateKey) => {
  const datesObject = {}

  rows?.forEach((row) => {
    const date = formatDate(row[dateKey] || new Date(), 'yyyyMMdd')

    if (!Object.keys(datesObject).includes(date)) {
      datesObject[date] = []
    }

    datesObject[date].push(row)
  })

  return datesObject
}

const getFormTitle = (formMode) => {
  switch (formMode) {
    case 'createTimer':
      return 'Start new timer'
    case 'createPosting':
      return 'Create new posting'
    case 'postTimer':
      return 'Post timer'
  }
}
